import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CheckboxLine,
  FilterSelect,
  FormField,
  FormGrid,
  HpButton,
  HpCardHeader,
  HpMultiselect,
  HpTag,
  IconButton,
} from '@hp/ui';
import { useCan } from '../../api/me';
import { trpc } from '../../api/trpc';
import { ErrorAlert } from '../../components/error-alert';
import { fmtInt } from '../../lib/format';
import { useLeadErrorText, useNeedsRuleText } from './error-text';
import { isOwnerSource, marketOfAccount, OUTCOME_TONE } from './labels';
import { TraceList } from './lead-detail';
import { CampaignLine, CampaignOptionList, CampaignPicker, ProjectOptionList, type Picked } from './rule-pickers';
import {
  invalidateLeads,
  useCampaignOptions,
  useChannelOptions,
  useOwners,
  usePipelines,
  type CatalogRow,
  type DryRunInput,
  type MarketDefault,
  type RuleSaveInput,
} from './queries';

/** What the editor panel is open for: a new rule (campaign preselected or still to pick) or a row's rule. */
export type EditorTarget = { kind: 'new'; row: CatalogRow | null } | { kind: 'edit'; row: CatalogRow };
/**
 * The same editor for a market default (tab "General rules"): no campaign, no Kampaň / Poptaný projekt,
 * pipeline and stage may stay empty, saved through `leads.defaults.save`.
 */
export type DefaultEditorTarget = { kind: 'default'; rule: MarketDefault };

type OptionMode = 'create' | 'existing' | 'none';

/** Kampaň defaults for a campaign without a rule: its confirmed analytics option, else a new option named after it. */
function optionDefaults(row: CatalogRow | null): { mode: OptionMode; picked: Picked | null } {
  if (row?.rule) {
    const o = row.rule.campaignOption;
    return o ? { mode: 'existing', picked: { id: o.id, label: o.label } } : { mode: 'none', picked: null };
  }
  if (row?.mappedOptionId) return { mode: 'existing', picked: { id: row.mappedOptionId, label: null } };
  return { mode: 'create', picked: null };
}

/** The rule editor: an `hp-card` panel above the table with Save / Cancel in its header. */
export function RuleEditor({ target, onClose }: { target: EditorTarget | DefaultEditorTarget; onClose: () => void }) {
  const { t } = useTranslation(['leads', 'common']);
  const describe = useLeadErrorText();
  const canManage = useCan()('leads.manage');
  const owners = useOwners();
  const pipelines = usePipelines();
  const marketDefault = target.kind === 'default' ? target.rule : null;
  const startRow = target.kind === 'default' ? null : target.row;
  const rule = target.kind === 'edit' ? target.row.rule : marketDefault;

  const [row, setRow] = useState<CatalogRow | null>(startRow);
  const [ownerIds, setOwnerIds] = useState<number[]>(rule?.owners.map((o) => o.userPdId) ?? []);
  const [pipelineId, setPipelineId] = useState<number | null>(rule?.pipeline?.pdId ?? null);
  const [stageId, setStageId] = useState<number | null>(rule?.stage?.pdId ?? null);
  const initialOption = optionDefaults(startRow);
  const [optionMode, setOptionMode] = useState<OptionMode>(initialOption.mode);
  const [option, setOption] = useState<Picked | null>(initialOption.picked);
  const [project, setProject] = useState<Picked | null>(
    rule?.projectOption ? { id: rule.projectOption.id, label: rule.projectOption.label } : null,
  );
  const [respectExisting, setRespectExisting] = useState(rule?.respectExistingOwner ?? true);
  const [active, setActive] = useState(rule?.active ?? true);
  const [picking, setPicking] = useState<'option' | 'project' | null>(null);
  const cardRef = useRef<HTMLElement>(null);

  // The panel opens above a long table: bring it into view and put the keyboard on its first field.
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    card.scrollIntoView({ block: 'start', behavior: 'smooth' });
    const first = card.querySelector<HTMLElement>(
      '.hp-card__body input:not([disabled]), .hp-card__body summary, .hp-card__body select',
    );
    (first ?? card).focus({ preventScroll: true });
  }, []);

  // A mapped option arrives as a bare id; HP names options after the campaigns, so a search by the name finds its label.
  const needsLabel = optionMode === 'existing' && option !== null && option.label === null && row !== null;
  const labelLookup = useCampaignOptions(row?.campaign.name ?? '', needsLabel);
  const optionLabel =
    option?.label ??
    labelLookup.data?.find((o) => o.id === option?.id)?.label ??
    (option === null
      ? null
      : labelLookup.isFetching
        ? t('common:actions.loading')
        : t('rules.unknownId', { id: option.id }));

  // Close first: the refetch bumps the rule's version, which would otherwise read as a change made elsewhere.
  const closeAndRefresh = () => {
    onClose();
    return invalidateLeads();
  };
  const saveRule = useMutation(trpc.leads.rules.save.mutationOptions({ onSuccess: closeAndRefresh }));
  const saveDefault = useMutation(trpc.leads.defaults.save.mutationOptions({ onSuccess: closeAndRefresh }));
  const save = marketDefault ? saveDefault : saveRule;

  const ownerName = useMemo(() => {
    const names = new Map<number, string>();
    for (const o of rule?.owners ?? []) if (o.name) names.set(o.userPdId, o.name);
    for (const o of owners.data ?? []) names.set(o.pdId, o.name);
    return (id: number) => names.get(id) ?? t('rules.unknownId', { id });
  }, [owners.data, rule, t]);
  const activeIds = owners.data ? new Set(owners.data.map((o) => o.pdId)) : null;
  // A pipeline or stage that is no longer active is not offered by the selects: treat it as unset, so Save asks for a new one.
  const pipeline = pipelines.data?.find((p) => p.pdId === pipelineId);
  const pipelineValue = pipelines.data && !pipeline ? null : pipelineId;
  const stages = pipeline?.stages ?? [];
  const stageValue =
    pipelineValue === null ? null : pipelines.data && !stages.some((s) => s.pdId === stageId) ? null : stageId;

  const pickCampaign = (next: CatalogRow | null) => {
    setRow(next);
    const d = optionDefaults(next);
    setOptionMode(d.mode);
    setOption(d.picked);
    setPicking(null);
  };
  const changeOwners = (values: string[]) => {
    const ids = values.map(Number);
    setOwnerIds([...ownerIds.filter((id) => ids.includes(id)), ...ids.filter((id) => !ownerIds.includes(id))]);
  };
  const move = (index: number, by: -1 | 1) => {
    const next = [...ownerIds];
    const [a, b] = [next[index], next[index + by]];
    if (a === undefined || b === undefined) return;
    next[index] = b;
    next[index + by] = a;
    setOwnerIds(next);
  };

  // A market default may stay without a pipeline and stage (the channel then cannot go live).
  const missing = marketDefault
    ? null
    : row === null
      ? 'campaign'
      : pipelineValue === null || stageValue === null
        ? 'target'
        : optionMode === 'existing' && option === null
          ? 'option'
          : null;
  const submit = () => {
    if (marketDefault) {
      saveDefault.mutate({
        id: marketDefault.id,
        kind: 'market_default',
        // Always set on a market default; were it missing, the API answers LEAD_RULE_INVALID (shown above).
        marketCode: marketDefault.marketCode ?? undefined,
        ownerUserPdIds: ownerIds,
        pipelinePdId: pipelineValue,
        stagePdId: stageValue,
        campaignOption: { kind: 'none' },
        projectOptionId: null,
        respectExistingOwner: respectExisting,
        active: marketDefault.active,
      });
      return;
    }
    if (row === null || pipelineValue === null || stageValue === null) return;
    const campaignOption: RuleSaveInput['campaignOption'] =
      optionMode === 'create'
        ? { kind: 'create', label: row.campaign.name }
        : optionMode === 'existing' && option
          ? { kind: 'existing', id: option.id }
          : { kind: 'none' };
    saveRule.mutate({
      id: rule?.id,
      kind: 'campaign',
      adCampaignId: row.campaign.id,
      ownerUserPdIds: ownerIds,
      pipelinePdId: pipelineValue,
      stagePdId: stageValue,
      campaignOption,
      projectOptionId: project?.id ?? null,
      respectExistingOwner: respectExisting,
      active,
    });
  };

  const title = marketDefault
    ? t('general.defaults.editorTitle', { market: marketDefault.marketLabel ?? marketDefault.marketCode ?? '' })
    : rule && row
      ? row.campaign.name
      : t('editor.newTitle');
  return (
    <section className="hp-card" ref={cardRef} tabIndex={-1} aria-label={title}>
      <HpCardHeader
        title={title}
        subtitle={t(marketDefault ? 'general.defaults.editorSubtitle' : 'editor.subtitle')}
        icon={rule ? 'edit' : 'plus'}
      >
        <HpButton variant="quiet" onClick={onClose} disabled={save.isPending}>
          {t('common:actions.cancel')}
        </HpButton>
        <HpButton
          variant="primary"
          icon="check"
          busy={save.isPending}
          disabled={missing !== null}
          title={missing ? t(`editor.missing.${missing}`) : undefined}
          onClick={submit}
        >
          {t('common:actions.save')}
        </HpButton>
      </HpCardHeader>
      <ErrorAlert
        error={save.error ?? owners.error ?? pipelines.error}
        describe={describe}
        className="bo-alert--inset"
      />
      <div className="hp-card__body bo-sections">
        {missing && (
          <p className="bo-hint" role="status">
            {t(`editor.missing.${missing}`)}
          </p>
        )}
        {!marketDefault && (
          <fieldset className="bo-fieldset">
            <legend>{t('editor.field.adCampaignId')}</legend>
            {row ? (
              <span className="bo-row">
                <CampaignLine row={row} />
                {target.kind === 'new' && (
                  <HpButton variant="quiet" icon="edit" className="bo-button--sm" onClick={() => pickCampaign(null)}>
                    {t('editor.change')}
                  </HpButton>
                )}
              </span>
            ) : (
              <CampaignPicker onPick={pickCampaign} />
            )}
          </fieldset>
        )}

        <fieldset className="bo-fieldset">
          <legend>{t('editor.field.ownerUserPdIds')}</legend>
          <div className="bo-sections">
            <HpMultiselect
              label={t('editor.ownersPick')}
              allLabel={t('editor.ownersNone')}
              heading={t('editor.ownersHeading')}
              hint={t('editor.ownersPopoverHint')}
              searchPlaceholder={t('editor.ownersSearch')}
              emptyLabel={t('editor.ownersEmpty')}
              items={(owners.data ?? []).map((o) => ({
                value: String(o.pdId),
                label: o.name,
                description: o.email ?? undefined,
              }))}
              selected={ownerIds.map(String)}
              onChange={changeOwners}
            />
            {ownerIds.length > 0 && (
              <ol className="bo-owner-order" aria-label={t('editor.ownersOrder')}>
                {ownerIds.map((id, i) => (
                  <li key={id} className="bo-owner-order__item">
                    <span className="bo-mono">{fmtInt(i + 1)}.</span>
                    <span>{ownerName(id)}</span>
                    {activeIds !== null && !activeIds.has(id) && (
                      <HpTag tone="danger">{t('rules.status.owner_inactive')}</HpTag>
                    )}
                    <IconButton
                      icon="chevronLeft"
                      aria-label={t('editor.moveEarlier', { name: ownerName(id) })}
                      title={t('editor.moveEarlier', { name: ownerName(id) })}
                      disabled={i === 0}
                      onClick={() => move(i, -1)}
                    />
                    <IconButton
                      icon="chevronRight"
                      aria-label={t('editor.moveLater', { name: ownerName(id) })}
                      title={t('editor.moveLater', { name: ownerName(id) })}
                      disabled={i === ownerIds.length - 1}
                      onClick={() => move(i, 1)}
                    />
                    <IconButton
                      icon="close"
                      aria-label={t('editor.ownerRemove', { name: ownerName(id) })}
                      title={t('editor.ownerRemove', { name: ownerName(id) })}
                      onClick={() => setOwnerIds(ownerIds.filter((o) => o !== id))}
                    />
                  </li>
                ))}
              </ol>
            )}
            <small className="bo-hint">
              {ownerIds.length > 0
                ? t('editor.ownersHint')
                : t(marketDefault ? 'general.defaults.ownersEmptyHint' : 'editor.ownersEmptyHint')}
            </small>
          </div>
        </fieldset>

        <FormGrid>
          <FormField label={t('editor.field.pipelinePdId')}>
            <select
              value={pipelineValue ?? ''}
              onChange={(e) => {
                setPipelineId(e.target.value === '' ? null : Number(e.target.value));
                setStageId(null);
              }}
            >
              <option value="">{t('editor.choose')}</option>
              {(pipelines.data ?? []).map((p) => (
                <option key={p.pdId} value={p.pdId}>
                  {p.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={t('editor.field.stagePdId')}>
            <select
              value={stageValue ?? ''}
              disabled={pipelineValue === null}
              onChange={(e) => setStageId(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">{t('editor.choose')}</option>
              {stages.map((s) => (
                <option key={s.pdId} value={s.pdId}>
                  {s.name}
                </option>
              ))}
            </select>
          </FormField>
        </FormGrid>
        {marketDefault && <small className="bo-hint">{t('general.defaults.targetHint')}</small>}

        {!marketDefault && (
          <>
            <fieldset className="bo-fieldset">
              <legend>{t('editor.field.campaignOption')}</legend>
              <div className="bo-sections">
                <CheckboxLine
                  type="radio"
                  name="lead-rule-campaign-option"
                  checked={optionMode === 'create'}
                  disabled={row === null}
                  onChange={() => setOptionMode('create')}
                >
                  {t('editor.optionCreate', { name: row?.campaign.name ?? '…' })}
                </CheckboxLine>
                {optionMode === 'create' && <small className="bo-hint">{t('editor.optionCreateHint')}</small>}
                <CheckboxLine
                  type="radio"
                  name="lead-rule-campaign-option"
                  checked={optionMode === 'existing'}
                  onChange={() => {
                    setOptionMode('existing');
                    if (option === null) setPicking('option');
                  }}
                >
                  {t('editor.optionExisting')}
                </CheckboxLine>
                {optionMode === 'existing' &&
                  (picking === 'option' ? (
                    <CampaignOptionList
                      onPick={(o) => {
                        setOption(o);
                        setPicking(null);
                      }}
                      onCancel={() => setPicking(null)}
                    />
                  ) : (
                    <span className="bo-row">
                      <span className={option ? 'bo-strong' : 'bo-muted'}>
                        {optionLabel ?? t('editor.optionNotPicked')}
                      </span>
                      <HpButton
                        variant="quiet"
                        icon="search"
                        className="bo-button--sm"
                        onClick={() => setPicking('option')}
                      >
                        {option ? t('editor.change') : t('editor.pick')}
                      </HpButton>
                    </span>
                  ))}
                <CheckboxLine
                  type="radio"
                  name="lead-rule-campaign-option"
                  checked={optionMode === 'none'}
                  onChange={() => setOptionMode('none')}
                >
                  {t('editor.optionNone')}
                </CheckboxLine>
              </div>
            </fieldset>

            <fieldset className="bo-fieldset">
              <legend>{t('editor.field.projectOptionId')}</legend>
              {picking === 'project' ? (
                <ProjectOptionList
                  noneLabel={t('editor.projectNone')}
                  onPick={(o) => {
                    setProject(o);
                    setPicking(null);
                  }}
                  onCancel={() => setPicking(null)}
                />
              ) : (
                <span className="bo-row">
                  <span className={project ? 'bo-strong' : 'bo-muted'}>
                    {project ? (project.label ?? t('rules.unknownId', { id: project.id })) : t('editor.projectNone')}
                  </span>
                  <HpButton
                    variant="quiet"
                    icon="search"
                    className="bo-button--sm"
                    onClick={() => setPicking('project')}
                  >
                    {t('editor.change')}
                  </HpButton>
                </span>
              )}
            </fieldset>
          </>
        )}

        <div className="bo-sections">
          <CheckboxLine checked={respectExisting} onChange={(e) => setRespectExisting(e.target.checked)}>
            {t('editor.respectExistingOwner')}
          </CheckboxLine>
          <small className="bo-hint">{t('editor.respectExistingOwnerHint')}</small>
          {!marketDefault && (
            <>
              <CheckboxLine checked={active} onChange={(e) => setActive(e.target.checked)}>
                {t('editor.active')}
              </CheckboxLine>
              <small className="bo-hint">{t('editor.activeHint')}</small>
            </>
          )}
        </div>

        {canManage && (marketDefault || row) && (
          <DryRunLine
            market={marketDefault?.marketCode ?? null}
            campaign={marketDefault ? null : (row?.campaign ?? null)}
          />
        )}
      </div>
    </section>
  );
}

/**
 * "Where would a lead of this campaign go now?": a test contact through `rules.dryRun` (the SAVED rules,
 * a live Pipedrive person search, nothing written) with the outcome and the decision trace.
 */
function DryRunLine({
  market,
  campaign,
}: {
  /** The market default being edited; null for a campaign rule. */
  market: string | null;
  campaign: CatalogRow['campaign'] | null;
}) {
  const { t } = useTranslation(['leads', 'common']);
  const describe = useLeadErrorText();
  const needsRuleText = useNeedsRuleText();
  const channels = useChannelOptions();
  const owners = useOwners();
  const pipelines = usePipelines();
  // The market's Meta channel by default; a campaign rule takes the market of its ad account (else Czech).
  const [channel, setChannel] = useState(`meta-${(market ?? marketOfAccount(campaign?.accountName)).toLowerCase()}`);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const run = useMutation(trpc.leads.rules.dryRun.mutationOptions());
  const channelKind = channels.data?.find((c) => c.code === channel)?.kind;
  // The market in the label too: the result names the market default of the channel's market.
  const options = channels.data?.map((c) => ({
    code: c.code,
    label: `${c.label} · ${c.marketLabel ?? c.marketCode}`,
  })) ?? [{ code: channel, label: channel }];

  const submit = () => {
    if (!isChannelCode(channel)) return;
    // Only a Meta lead carries a campaign id (web leads never match a campaign rule), and only Meta ids are matched.
    const metaCampaign = campaign?.provider === 'meta' && channelKind !== 'web';
    run.mutate({
      channel,
      campaignExternalId: metaCampaign ? campaign.externalId : undefined,
      campaignName: campaign?.name,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      name: name.trim() || undefined,
    });
  };

  const outcome = run.data?.decision.outcome;
  let line: string | null = null;
  if (outcome?.kind === 'routed') {
    const pipeline = pipelines.data?.find((p) => p.pdId === outcome.pipelinePdId);
    line = t('editor.dryRun.routed', {
      owner:
        owners.data?.find((o) => o.pdId === outcome.ownerUserPdId)?.name ??
        t('rules.unknownId', { id: outcome.ownerUserPdId }),
      source: isOwnerSource(outcome.ownerSource)
        ? t(`general.ownerSource.${outcome.ownerSource}`)
        : outcome.ownerSource,
      pipeline: pipeline?.name ?? t('rules.unknownId', { id: outcome.pipelinePdId }),
      stage:
        pipeline?.stages.find((s) => s.pdId === outcome.stagePdId)?.name ??
        t('rules.unknownId', { id: outcome.stagePdId }),
    });
  } else if (outcome?.kind === 'needs_rule') {
    line = needsRuleText(outcome.reason);
  }

  return (
    <fieldset className="bo-fieldset">
      <legend>{t(market ? 'editor.dryRun.titleDefault' : 'editor.dryRun.title')}</legend>
      <div className="bo-sections">
        <p className="bo-hint">{t('editor.dryRun.savedOnly')}</p>
        {campaign && campaign.provider !== 'meta' && <p className="bo-hint">{t('editor.dryRun.notMeta')}</p>}
        <form
          className="bo-inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <FormField label={t('editor.dryRun.channel')}>
            <FilterSelect value={channel} onChange={(e) => setChannel(e.target.value)}>
              {options.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </FilterSelect>
          </FormField>
          <FormField label={t('editor.dryRun.email')}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </FormField>
          <FormField label={t('editor.dryRun.phone')}>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </FormField>
          <FormField label={t('editor.dryRun.name')}>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <div className="bo-inline-form__submit">
            <HpButton type="submit" variant="secondary" icon="play" busy={run.isPending}>
              {t('editor.dryRun.try')}
            </HpButton>
          </div>
        </form>
        <small className="bo-hint">{t('editor.dryRun.hint')}</small>
        <ErrorAlert error={run.error} describe={describe} />
        {run.data && outcome && (
          <div className="bo-sections" role="status">
            <p className="bo-row">
              <HpTag tone={OUTCOME_TONE[outcome.kind]}>{t(`received.outcome.${outcome.kind}`)}</HpTag>
              {line && <span>{line}</span>}
            </p>
            {outcome.kind === 'routed' && outcome.routedByDefault && (
              <p className="bo-hint">{t('editor.dryRun.byDefault')}</p>
            )}
            <TraceList trace={run.data.decision.trace} />
          </div>
        )}
      </div>
    </fieldset>
  );
}

const CHANNEL_CODES = ['web-cz', 'web-es', 'meta-cz', 'meta-es'] as const satisfies ReadonlyArray<
  DryRunInput['channel']
>;
function isChannelCode(value: string): value is DryRunInput['channel'] {
  return (CHANNEL_CODES as readonly string[]).includes(value);
}
